import styled from "styled-components";
import {Button, type MenuProps} from "@mui/material";
import {SHEET_READING_WIDTH} from "components/CardSheet/CardSheet.styles";
import {brand, goldEdgeFor, radii} from "../../theme/tokens";

/**
 * Externalized styles for {@link ./OptionsSheet} (ui#214) — the body rendered inside the generic
 * {@link ../../components/CardSheet}: the Delivery tabs + recipient field, the option toggle rows
 * (password / download limit / zero-knowledge) with their revealed inputs, the expiry choices, and
 * the sticky footer (plan + hosted limit + Upgrade/Save actions). Dumb, token-driven (epic #26): every
 * colour reads the design-system tokens, so light + dark fall out for free; nothing is hardcoded.
 */

/**
 * The half-gap every stacked block in this sheet family carries, top and bottom.
 *
 * ONE definition rather than the two identical `padding: 18px 0` rules this file used to hold, because
 * {@link Section} and {@link OptionBlock} are not two spacings that happen to agree — they are the one
 * rhythm ui#240 asked for when it removed the 1px dividers between rows ("whitespace instead of
 * rules"). The number itself was the implementer's answer to that, not a value the owner named.
 *
 * Adjacent blocks each contribute it, so what a reader SEES between two rows is twice this. It was 18,
 * i.e. 36px between rows, measured at 375px on 2026-09-03 across both sheets that use these blocks (the
 * transfer options sheet and the personalization builder, which imports Section from here). The owner
 * called that "too much space between the options" on the same date, so it is 12 — 24px between rows in
 * both sheets. This is the only place to change it, in either direction.
 */
const STACK_HALF_GAP_PX = 12;

/**
 * Caps this sheet's stacked controls at the sheet family's reading measure (ui#2060).
 *
 * The ~552px body let every field run gutter to gutter, which the owner read as broken on a 14"
 * screen. Billing (ui#1816) already answers this with
 * {@link ../../components/CardSheet/CardSheet.styles SHEET_READING_WIDTH}; this sheet keeps the cap
 * LEFT-ALIGNED rather than centered like Billing's, because its section labels share the head's left
 * edge — Billing centers a stack of tinted cards, which has no edge to keep.
 *
 * Here rather than on {@link Section}/{@link OptionBlock}: the personalization builder imports
 * Section for its two-column wide sheet, where a 480px cap would be a different (and wrong) layout
 * decision.
 */
export const Column = styled.div`
	width: 100%;
	max-width: ${SHEET_READING_WIDTH};
`;

/** A titled section; sections are grouped by whitespace, no rules (ui#240). */
export const Section = styled.section`
	padding: ${STACK_HALF_GAP_PX}px 0;

	&:first-of-type {
		padding-top: 2px;
	}
`;

/** Mono-ish uppercase section label above the control (Delivery / Expiry). */
export const SectionLabel = styled.div`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink2};
	margin-bottom: 12px;
`;

/**
 * A section label sharing its line with a trailing control — today the recipients field's inline
 * Upgrade nudge (ui#2524). The label's own bottom margin moves onto the row so the section below sits
 * exactly where it does under a bare label, whether or not the control is there to see.
 *
 * The label is given a zero min-width so it may shrink rather than push the row past the card, and the
 * row wraps so a label too long to share the line takes the control onto the next one instead of
 * overflowing. German carries the longest label ("Per E-Mail an Personen") and 375px the narrowest
 * card, which is where the two would meet.
 */
export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px;
	margin-bottom: 12px;

	> ${SectionLabel} {
		margin-bottom: 0;
		min-width: 0;
	}
`;

/**
 * The unified recipient chip field. Always present since ui#890 dropped the Delivery tabs, so it is
 * plain static layout: the ui#270 mount animation it used to carry was the TAB SWITCH's entrance, and
 * with nothing to switch it would replay on every sheet open, out of step with the sections around it.
 */
export const RecipientField = styled.div`
	margin-top: 14px;
`;

/** One option toggle block (password / download limit / zero-knowledge); grouped by whitespace (ui#240). */
export const OptionBlock = styled.div`
	padding: ${STACK_HALF_GAP_PX}px 0;
`;

/** The toggle row: label + description on the left, the switch on the right. */
export const OptionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
`;

export const OptionMeta = styled.div`
	flex: 1;
	min-width: 0;
`;

export const OptionName = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
	display: flex;
	align-items: center;
	gap: 6px;
`;

/**
 * The info glyph that reveals an option row's hint (api#443). A real `<button>` rather than a styled
 * span so it is reachable by keyboard without a tabIndex hack — MUI's Tooltip binds focus/blur on the
 * child, which is what makes the hint appear for keyboard users as well as on hover.
 *
 * 24px is below the 44px touch target the rest of the sheet uses, deliberately: the hint is purely
 * supplementary (its copy is also the button's description) and a full-size target next to the row's
 * switch would compete with the control that actually does something.
 */
export const HintButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	padding: 0;
	border: 1px solid ${({theme}) => theme.tokens.fieldLine};
	border-radius: ${radii.pill};
	background: transparent;
	color: ${({theme}) => theme.tokens.ink3};
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	cursor: help;

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
		border-color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const OptionDesc = styled.div`
	font-size: 12px;
	color: ${({theme}) => theme.tokens.ink2};
	margin-top: 2px;
`;

/** The input area revealed beneath a toggle once it is on (password field / download-limit stepper). */
export const OptionReveal = styled.div`
	margin-top: 14px;
`;

/**
 * Helper copy under a sheet field: the Message field's "the default is editable" note (ui#216) and the
 * download-password bounds (ui#384). `$error` paints the line in the error ink for a refusal, so the
 * same element carries the hint and the error and nothing shifts when one becomes the other.
 */
export const FieldHint = styled.p<{$error?: boolean}>`
	margin: 8px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: ${({theme, $error}) => $error ? theme.tokens.errorInk : theme.tokens.ink2};
`;

/** Spacing between the sheet's Title and Message inputs. */
export const ComposeField = styled.div`
	& + & {
		margin-top: 14px;
	}
`;

/**
 * The pill switch (design `.opt-item .switch`): the toggle role's track when on — SECONDARY signal teal
 * (owner, ui#823, superseding the design file's orange), or a signed-in Premium account's colour shaded
 * until it clears 3:1 (ui#2935) — with a sliding white knob. The theme's MuiSwitch override paints the same
 * `toggleOn`, so both switch stacks agree.
 */
export const Switch = styled.button<{$on: boolean}>`
	width: 40px;
	height: 23px;
	border-radius: ${radii.pill};
	background: ${({theme, $on}) => ($on ? theme.tokens.toggleOn : theme.tokens.fieldLine)};
	position: relative;
	border: none;
	cursor: pointer;
	flex: none;
	transition: background 0.18s;
	padding: 0;

	&:disabled {
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	span {
		position: absolute;
		top: 2.5px;
		left: ${({$on}) => ($on ? "19.5px" : "2.5px")};
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: ${brand.white};
		transition: left 0.18s;
		box-shadow: 0 1px 3px color-mix(in srgb, ${brand.navy} 30%, transparent);
	}
`;

/**
 * One rung inside the expiry listbox: the retention label, with any upsell pill pushed to the trailing
 * edge so the pills line up down the menu instead of floating at each label's own width.
 *
 * The rung's selected/hover/focus treatment is MUI's own `MenuItem` — deliberately not restated here.
 * A listbox option that paints its own selected state disagrees with the one the roving focus draws,
 * and the two then drift apart in exactly one theme.
 */
export const ExpiryOption = styled.span`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
`;

/**
 * The expiry listbox's own scroll box (ui#1201).
 *
 * Fifteen rungs stack far past the field, and MUI would otherwise let the menu grow to the viewport —
 * which is the chip wall this picker replaced, merely stood on its side. Capped so the control stays
 * compact and scrolls instead. It lives in the styles file rather than at the `<BaseSelect>` call site
 * because a component file carries markup and behavior only (CLAUDE.md, "Externalized styles").
 */
export const expiryMenuProps: Partial<MenuProps> = {slotProps: {paper: {sx: {maxHeight: 320}}}};

/** The sticky-footer layout: plan + limit followed by its ordered actions. */
export const FooterRoot = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;

	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column;
	}
`;

export const FooterInfo = styled.div`
	min-width: 0;
`;

export const FooterPlan = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const FooterLimit = styled.div`
	font-size: 12px;
	color: ${({theme}) => theme.tokens.ink2};
	margin-top: 2px;
`;

/** Upgrade precedes Save in DOM, visual, and keyboard order at every viewport width. */
export const FooterActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	flex: none;

	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column;
		width: 100%;
	}
`;

/** Shared MUI footer action treatment; variants and palette roles remain explicit at each call site. */
export const FooterAction = styled(Button)`
	min-height: 44px;
	border-radius: ${radii.control};

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		box-shadow: 0 0 0 4px ${({theme}) => theme.tokens.ring};
	}

	@media (max-width: 480px) {
		width: 100%;
	}
`;

/** Gold Upgrade treatment inherited from the shared footer action foundation. */
export const UpgradeFooterAction = styled(FooterAction)`
	&.MuiButton-contained.MuiButton-colorPrimary {
		background-color: ${brand.gold};
		color: ${brand.goldInk};
		/* Outline, not box-shadow: FooterAction's :focus-visible ring IS a box-shadow, and this rule
		   outranks it, so a shadow here would cost this button its focus indicator. */
		outline: 1px solid ${({theme}) => goldEdgeFor(theme.tokens)};
		outline-offset: -1px;
	}

	&.MuiButton-contained.MuiButton-colorPrimary:not(:disabled):hover {
		background-color: ${brand.goldDark};
	}
`;
